<template>
  <div class="folder-controls">
    <div class="folder-controls-left">
      <v-tooltip bottom>
        <template v-slot:activator="{ on, attrs }">
          <v-btn
            icon
            v-bind="attrs"
            v-on="on"
            @click="dialog = true"
            :color="filterType === 'include' && filterValues.length ? 'primary' : 'gray'"
          >
            <v-icon>mdi-filter-variant</v-icon>
          </v-btn>
        </template>
        <span>File filters</span>
      </v-tooltip>
      <v-dialog v-model="dialog"
                width="500"
      >
        <v-card>
          <v-card-title class="headline">File filter</v-card-title>
          <v-card-text>
            <v-radio-group v-model="filterType"
                           row
                           @change="saveFilterType"
            >
              <v-radio label="Display only"
                       value="include"
              ></v-radio>
              <v-radio label="Display all"
                       value="all"
              ></v-radio>
            </v-radio-group>
            <v-autocomplete
              :disabled="filterType === 'all'"
              v-model="filterValues"
              :items="fileExtensions"
              deletable-chips
              chips
              small-chips
              label="File extension filters"
              multiple
              @input="saveValue"
            ></v-autocomplete>
            <div class="paragraph-two bold margin-top-two">Edit extensions list</div>
            <div class="add-file-extension">
              <div style="width: 50%"
                   class="margin-top-one"
              >
                <v-form v-model="isFormValid"
                        @submit.prevent
                >
                  <v-text-field
                    label="Add file extension"
                    v-model="fileExtension"
                    append-icon="mdi-plus-circle"
                    @click:append="addFileExtension"
                    @keydown="addFileExtensionByEnter"
                    aria-autocomplete="none"
                    :rules="[rules.dotExtension]"
                    hint="Type extension with dot, for example .mov"
                  ></v-text-field>
                </v-form>
              </div>
              <div class="margin-top-one">
                <v-autocomplete
                  label="Remove file extension"
                  v-model="fileExtensions"
                  :items="fileExtensions"
                  small-chips
                  deletable-chips
                  multiple
                  aria-autocomplete="none"
                  hide-selected
                  hide-no-data
                  @input="removeFileExtension"
                ></v-autocomplete>
              </div>
            </div>
          </v-card-text>
        </v-card>
      </v-dialog>

      <slot name="quickView"></slot>

      <v-tooltip bottom>
        <template v-slot:activator="{ on, attrs }">
          <v-btn icon
                 v-bind="attrs"
                 v-on="on"
                 @click="$emit('refresh-folder')"
          >
            <v-icon>mdi-file-refresh-outline</v-icon>
          </v-btn>
        </template>
        <span>Refresh folder</span>
      </v-tooltip>

      <v-tooltip bottom>
        <template v-slot:activator="{ on, attrs }">
          <v-btn icon
                 v-bind="attrs"
                 v-on="on"
                 :color="'gray'"
                 @click="onSelectPaths"
          >
            <v-icon>mdi-file-plus-outline</v-icon>
          </v-btn>
        </template>
        <span>Add file paths</span>
      </v-tooltip>
      <v-dialog v-model="pathDialog"
                width="500"
                persistent
      >
        <v-card>
          <v-card-title class="headline">Add file paths</v-card-title>
          <v-card-text>
            <v-form 
              ref     = "pathsForm" 
              v-model = "isPathsFormValid"
            >
              <v-text-field
                autofocus
                label="Path to video"
                v-model="videoPath"
                @keydown="addPathByEnter"
                :rules="[rules.required]"
              ></v-text-field>
              <v-text-field
                label="Path to subtitles"
                v-model="subtitlesPath"
                @keydown="addPathByEnter"
              ></v-text-field>
            </v-form>
          </v-card-text>
          <v-card-actions>
            <v-spacer></v-spacer>
            <v-btn v-if="!isVideoLoading"
                   text
                   @click="pathDialog = false"
            > Cancel</v-btn>
            <v-btn color="primary"
                   text
                   @click="addPaths"
            >
              <span v-if="isVideoLoading">
                <v-progress-circular indeterminate
                                     color="primary"
                                     :size="24"
                ></v-progress-circular>
              </span>
              <span v-if="!isVideoLoading">Load video</span>
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>

      <AudioPathConfig @load-audio="$emit('load-audio')"></AudioPathConfig>
    </div>
    <div class="folder-controls-right">
      <slot name="rightButtons"></slot>
    </div>
  </div>
</template>

<script>
import { SET_FILE_BROWSER_FILTER_TYPE, SET_FILE_BROWSER_FILTER_VALUES } from '@/utils/mutation-types';
import axios from 'axios';
import { ConnectionService } from '@/services/connection.service';
import { mapState } from 'vuex';
import { BusService } from '@/services/bus.service';
import AudioPathConfig from '@/components/file-explorer/folder-controls/AudioPathConfig';
import {LEVELS, sendEphemeralSnack} from "@/store/utilities/snacks.utils";

export default {
  name:       'FolderControls',
  components: { AudioPathConfig },
  data:       function () {
    return {
      dialog:                      false,
      fileExtensions:              [],
      isFormValid:                 true,
      fileExtension:               null,
      fileExtensionsToRemoveValue: null,
      pathDialog:                  false,
      videoPath:                   null,
      subtitlesPath:               null,
      isPathsFormValid:            false,
      isVideoLoading:              false,
      videoLoadedSubscription:     null,
      rules:                       {
        dotExtension: (value) => {
          const pattern = /^\./;
          return !value || pattern.test(value) || 'Invalid extension format';
        },
        required: (value) => !!value || 'Required',
      },
    };
  },
  computed: {
    ...mapState(['videoElement']),
    filterValues: {
      get: function () {
        return this.$store.state.fileBrowserFilterValues;
      },
      set: function (newValue) {
        this.$store.commit(SET_FILE_BROWSER_FILTER_VALUES, newValue);
      },
    },
    filterType: {
      get: function () {
        return this.$store.state.fileBrowserFilterType;
      },
      set: function (newValue) {
        this.$store.commit(SET_FILE_BROWSER_FILTER_TYPE, newValue);
      },
    },
  },
  mounted() {
    this.videoLoadedSubscription = BusService.videoLoaded.subscribe(() => {
      this.isVideoLoading = false;
    });
    this.getFileExtensionsFromLocalStorage();
  },
  destroyed() {
    if (this.videoLoadedSubscription) this.videoLoadedSubscription.unsubscribe();
  },
  methods: {
    saveFilterType() {
      window.localStorage.setItem('glimpseFilterType', this.filterType);
    },
    saveValue() {
      window.localStorage.setItem('glimpseFileExtensionValues', JSON.stringify(this.filterValues));
    },
    addFileExtension() {
      if (this.isFormValid) {
        if (this.fileExtension) this.fileExtensions.push(this.fileExtension);
        this.fileExtension = null;
        window.localStorage.setItem('glimpseFileExtensionFilters', JSON.stringify(this.fileExtensions));
      }
    },
    addFileExtensionByEnter(event) {
      if (event.code === 'Enter') {
        this.addFileExtension();
      }
    },
    addPathByEnter(event) {
      if (event.code === 'Enter') {
        this.addPaths();
      }
    },
    removeFileExtension() {
      window.localStorage.setItem('glimpseFileExtensionFilters', JSON.stringify(this.fileExtensions));
      let fileExtensionsSet = new Set(this.fileExtensions);
      this.filterValues.forEach((value, index) => {
        if (!fileExtensionsSet.has(value)) {
          this.filterValues.splice(index, 1);
        }
      });
      window.localStorage.setItem('glimpseFileExtensionValues', JSON.stringify(this.filterValues));
    },
    getFileExtensionsFromLocalStorage() {
      if (!window.localStorage.getItem('glimpseFilterType')) {
        window.localStorage.setItem('glimpseFilterType', this.filterType);
      } else this.filterType = window.localStorage.getItem('glimpseFilterType');
      if (!window.localStorage.getItem('glimpseFileExtensionFilters')) {
        window.localStorage.setItem('glimpseFileExtensionFilters', JSON.stringify(this.fileExtensions));
      } else this.fileExtensions = JSON.parse(window.localStorage.getItem('glimpseFileExtensionFilters'));
      if (!window.localStorage.getItem('glimpseFileExtensionValues')) {
        window.localStorage.setItem('glimpseFileExtensionValues', JSON.stringify(this.filterValues));
      } else this.filterValues = JSON.parse(window.localStorage.getItem('glimpseFileExtensionValues'));
      this.fileExtensionsToRemoveValue = this.fileExtensions;
    },
    onSelectPaths() {
      if (this.$refs.pathsForm) {
        this.$refs.pathsForm.reset();
      }
      this.pathDialog = true;
    },
    addPaths() {
      if (this.isPathsFormValid) {
        this.isVideoLoading = true;
        const url = this.getUrl(this.videoPath, 0, 0, null);
        axios
          .get(url)
          .then(() => {
            this.$emit('paths', {
              videoPath:     this.videoPath,
              subtitlesPath: this.subtitlesPath,
            });
            this.dialog = false;
          })
          .catch(error => {
            if (error.response) {
              // The request was made and the server responded with a status code
              // that falls out of the range of 2xx
              sendEphemeralSnack(this.$store, LEVELS.ERROR, error.response.data);
            } else if (error.request) {
              // The request was made but no response was received
              // `error.request` is an instance of XMLHttpRequest in the browser 
              // and an instance of http.ClientRequest in node.js
              sendEphemeralSnack(this.$store, LEVELS.ERROR, error);
            } else {
              // Something happened in setting up the request that triggered an Error
              sendEphemeralSnack(this.$store, LEVELS.ERROR, error);
            }

            this.isVideoLoading = false;
          });
      }
    },
    getUrl(path, startFrame, frameCount, token) {
      let encodedPath = encodeURIComponent(path);

      let root = token ? '/link' + token : '/play';

      let res = screen.width + 'x' + screen.height + ',' + 800 + 'x' + 600;

      return ConnectionService.getUrl(
        root +
                    '/master.m3u8?path=' +
                    encodedPath +
                    '&startFrame=' +
                    startFrame +
                    '&frameCount=' +
                    frameCount +
                    '&res=' +
                    res
      );
    },
  },
};
</script>

<style scoped>
.folder-controls {
    display: flex;
    justify-content: space-between;
}

.folder-controls-left {
    display: flex;
    flex-direction: row;
}

.add-file-extension {
    display: flex;
    flex-direction: column;
}
</style>
